import {
  Avatar,
  Badge,
  Box,
  ProgressCircle,
  Separator,
  HStack,
  Icon,
  Text,
  VStack,
  IconButton,
  Flex,
} from '@chakra-ui/react'
import { AnimatedSection, useAnimationContext } from '@src/components/sco/AnimatedSection'
import { useDelayedText } from '@src/components/sco/TypingText'
import { ChevronLeftIcon } from '@src/components/sco/icons'
import { motion, AnimatePresence } from 'framer-motion'
import { useEffect, useState } from 'react'
import { LuCheck, LuMapPin, LuSearch } from 'react-icons/lu'

type InvestorData = {
  id: number
  name: string
  title: string
  location: string
  matches: string[]
  matchScore: number
  avatarUrl: string
}

type InvestorMatchExplanationEffect = 'none' | 'typing' | 'fade' | 'collapse'

const SearchInvestorsAnim = () => {
  const [showInputs, setShowInputs] = useState(true)
  const [showResults, setShowResults] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowInputs(false)
      setTimeout(() => {
        setShowResults(true)
      }, 500)
    }, 9000)

    return () => clearTimeout(timer)
  }, [])

  return (
    <AnimatedSection
      maxWidth={'600px'}
      width={'100%'}
      variants={{
        hidden: { opacity: 0, x: 20 },
        visible: {
          opacity: 1,
          x: 0,
          transition: {
            duration: 0.6,
            ease: [0.34, 1.56, 0.64, 1],
          },
        },
      }}
    >
      <motion.div
        animate={{ height: showResults ? '465px' : 'auto' }}
        initial={{ height: 'auto' }}
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
        transition={{
          duration: 0.5,
          ease: 'easeInOut',
        }}
      >
        <VStack
          align={'center'}
          bgColor={'colorPalette.container'}
          borderRadius={'xl'}
          boxShadow={'md'}
          gap={6}
          height={'100%'}
          overflow={'hidden'}
          padding={8}
          position={'relative'}
          width={'full'}
        >
          <AnimatePresence mode={'wait'}>
            {showInputs && (
              <motion.div
                exit={{ opacity: 0 }}
                initial={{ opacity: 1 }}
                style={{ width: '100%' }}
                transition={{ duration: 0.5 }}
              >
                <VStack align={'center'} gap={6} width={'full'}>
                  <AnimatedRaiseAmount />
                  <AnimatedIndustryDropdown />
                  <AnimatedLocationDropdown />
                </VStack>
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {showResults && (
              <motion.div
                animate={{ opacity: 1 }}
                initial={{ opacity: 0 }}
                style={{ width: '100%', height: '100%' }}
                transition={{ duration: 0.5 }}
              >
                <ScrollingResults />
              </motion.div>
            )}
          </AnimatePresence>
        </VStack>
      </motion.div>
    </AnimatedSection>
  )
}

const AnimatedRaiseAmount = () => {
  const { isVisible } = useAnimationContext()
  const matchExplanation = '$250,000'
  const [isActive, setIsActive] = useState(false)
  const [isComplete, setIsComplete] = useState(false)

  const delayedMatchExplanation = useDelayedText(matchExplanation, 200, isActive)

  useEffect(() => {
    if (!isVisible) {
      return
    }
    const timer = setTimeout(() => {
      setIsActive(true)
    }, 500)
    return () => clearTimeout(timer)
  }, [isVisible])

  useEffect(() => {
    if (delayedMatchExplanation === matchExplanation) {
      const timer = setTimeout(() => {
        setIsActive(false)
        setIsComplete(true)
      }, 500)
      return () => clearTimeout(timer)
    }
  }, [delayedMatchExplanation, matchExplanation])

  return (
    <VStack align={'flex-start'} gap={2} width={'full'}>
      <Text color={'colorPalette.fg'} textStyle={'sm'}>
        How much do you need to raise?
      </Text>
      <HStack
        bgColor={'colorPalette.bg'}
        border={'2px solid'}
        borderColor={isActive ? 'colorPalette.focusRing' : 'colorPalette.border'}
        borderRadius={'md'}
        gap={0}
        height={'48px'}
        padding={0}
        transition={'border-color 0.3s ease'}
        width={'full'}
      >
        <Box borderColor={'colorPalette.border'} borderRight={'1px solid'} padding={2} width={'64px'}>
          <Text textStyle={'label2xs'}>USD $</Text>
        </Box>
        <Box padding={2} width={'full'}>
          <Text>{delayedMatchExplanation}</Text>
        </Box>
      </HStack>
    </VStack>
  )
}

const AnimatedIndustryDropdown = () => {
  const { isVisible } = useAnimationContext()
  const [isActive, setIsActive] = useState(false)
  const industries = ['SaaS', 'FinTech', 'AI/ML']
  const [currentIndustryIndex, setCurrentIndustryIndex] = useState(0)
  const [completedIndustries, setCompletedIndustries] = useState<number[]>([])

  const currentIndustry = industries[currentIndustryIndex]
  const delayedIndustry = useDelayedText(currentIndustry, 100, isActive)

  useEffect(() => {
    if (!isVisible) {
      return
    }
    const timer = setTimeout(() => {
      setIsActive(true)
    }, 2500)
    return () => clearTimeout(timer)
  }, [isVisible])

  useEffect(() => {
    if (delayedIndustry === currentIndustry) {
      const timer = setTimeout(() => {
        setCompletedIndustries(prev => [...prev, currentIndustryIndex])
        if (currentIndustryIndex < industries.length - 1) {
          setCurrentIndustryIndex(prev => prev + 1)
        } else {
          setIsActive(false)
        }
      }, 500)
      return () => clearTimeout(timer)
    }
  }, [delayedIndustry, currentIndustry, currentIndustryIndex])

  return (
    <VStack align={'flex-start'} gap={2} width={'full'}>
      <Text color={'colorPalette.fg'} textStyle={'sm'}>
        Industries/Verticals:
      </Text>
      <HStack
        bgColor={'colorPalette.bg'}
        border={'2px solid'}
        borderColor={isActive ? 'colorPalette.focusRing' : 'colorPalette.border'}
        borderRadius={'md'}
        gap={0}
        height={'48px'}
        padding={0}
        transition={'border-color 0.3s ease'}
        width={'full'}
      >
        <Box borderColor={'colorPalette.border'} borderRight={'1px solid'} padding={2} width={'40px'}>
          <Icon asChild color={'colorPalette.border'}>
            <LuSearch />
          </Icon>
        </Box>
        <Box padding={2} width={'full'}>
          <HStack gap={2}>
            {industries.map((industry, index) => {
              const isComplete = completedIndustries.includes(index)
              const isCurrent = index === currentIndustryIndex
              const showText = isComplete || isCurrent

              if (!showText) {
                return null
              }

              return (
                <Box
                  key={industry}
                  bgColor={isComplete ? 'purpleLightAlpha.200' : 'transparent'}
                  border={isComplete ? '1px solid' : 'none'}
                  borderColor={'colorPalette.border'}
                  borderRadius={'md'}
                  paddingX={2}
                  paddingY={1}
                >
                  <Text color={'colorPalette.border'} fontSize={'sm'} opacity={isComplete ? 1 : 0.7}>
                    {isComplete ? industry : isCurrent ? delayedIndustry : ''}
                  </Text>
                </Box>
              )
            })}
          </HStack>
        </Box>
      </HStack>
    </VStack>
  )
}

const AnimatedLocationDropdown = () => {
  const { isVisible } = useAnimationContext()
  const [isActive, setIsActive] = useState(false)
  const locations = ['Columbus, OH']
  const [currentLocationIndex, setCurrentLocationIndex] = useState(0)
  const [completedLocations, setCompletedLocations] = useState<number[]>([])

  const currentLocation = locations[currentLocationIndex]
  const delayedLocation = useDelayedText(currentLocation, 100, isActive)

  useEffect(() => {
    if (!isVisible) {
      return
    }
    const timer = setTimeout(() => {
      setIsActive(true)
    }, 5500)
    return () => clearTimeout(timer)
  }, [isVisible])

  useEffect(() => {
    if (delayedLocation === currentLocation) {
      const timer = setTimeout(() => {
        setCompletedLocations(prev => [...prev, currentLocationIndex])
        setIsActive(false)
      }, 500)
      return () => clearTimeout(timer)
    }
  }, [delayedLocation, currentLocation, currentLocationIndex])

  return (
    <VStack align={'flex-start'} gap={2} width={'full'}>
      <Text color={'colorPalette.fg'} textStyle={'sm'}>
        Location:
      </Text>
      <HStack
        bgColor={'colorPalette.bg'}
        border={'2px solid'}
        borderColor={isActive ? 'colorPalette.focusRing' : 'colorPalette.border'}
        borderRadius={'md'}
        gap={0}
        height={'48px'}
        padding={0}
        transition={'border-color 0.3s ease'}
        width={'full'}
      >
        <Box borderColor={'colorPalette.border'} borderRight={'1px solid'} padding={2} width={'40px'}>
          <Icon asChild color={'colorPalette.border'}>
            <LuMapPin />
          </Icon>
        </Box>
        <Box padding={2} width={'full'}>
          <HStack gap={2}>
            {locations.map((location, index) => {
              const isComplete = completedLocations.includes(index)
              const isCurrent = index === currentLocationIndex
              const showText = isComplete || isCurrent

              if (!showText) {
                return null
              }

              return (
                <Box
                  key={location}
                  bgColor={isComplete ? 'purpleLightAlpha.200' : 'transparent'}
                  border={isComplete ? '1px solid' : 'none'}
                  borderColor={'colorPalette.border'}
                  borderRadius={'md'}
                  paddingX={2}
                  paddingY={1}
                >
                  <Text color={'colorPalette.border'} fontSize={'sm'} opacity={isComplete ? 1 : 0.7}>
                    {isComplete ? location : isCurrent ? delayedLocation : ''}
                  </Text>
                </Box>
              )
            })}
          </HStack>
        </Box>
      </HStack>
    </VStack>
  )
}

const InvestorProfile = ({ investor, onClose }: { investor: InvestorData; onClose: () => void }) => {
  return (
    <motion.div
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      initial={{ opacity: 0, x: 20 }}
      style={{ width: '100%', height: '100%' }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
    >
      <VStack
        align={'flex-start'}
        bgColor={'colorPalette.subtle'}
        borderRadius={'md'}
        gap={6}
        height={'full'}
        padding={4}
        width={'full'}
      >
        <HStack justify={'flex-start'} width={'full'}>
          <IconButton
            aria-label={'Back to results'}
            color={'colorPalette.border'}
            variant={'ghost'}
            onClick={onClose}
          >
            <ChevronLeftIcon />
          </IconButton>
          <Text color={'colorPalette.fg'} textStyle={'md'}>
            Back to Results
          </Text>
        </HStack>

        <VStack align={'flex-start'} gap={4} width={'full'}>
          <HStack gap={4}>
            <Avatar.Root borderRadius={'lg'} size={'xl'}>
              <Avatar.Fallback borderRadius={'lg'} />
              <Avatar.Image borderRadius={'lg'} src={investor.avatarUrl} />
            </Avatar.Root>
            <VStack align={'flex-start'} gap={1}>
              <Text color={'white'} textStyle={'labelLg'}>
                {investor.name}
              </Text>
              <Text color={'colorPalette.border'} textStyle={'labelMd'}>
                {investor.title}
              </Text>
              <Text color={'colorPalette.border'} textStyle={'labelSm'}>
                {investor.location}
              </Text>
            </VStack>
          </HStack>

          <VStack align={'flex-start'} gap={2}>
            <Text color={'white'} textStyle={'labelMd'}>
              Match Score
            </Text>
            <ProgressCircle.Root size={'xl' as any} value={investor.matchScore}>
              <ProgressCircle.Circle>
                <ProgressCircle.Track />
                <ProgressCircle.Range stroke={'#45BDB5'} />
              </ProgressCircle.Circle>
            </ProgressCircle.Root>
          </VStack>

          <VStack align={'flex-start'} gap={2}>
            <Text color={'white'} textStyle={'labelMd'}>
              Matching Industries
            </Text>
            <HStack gap={2} wrap={'wrap'}>
              {investor.matches.map((match, idx) => (
                <Badge key={idx} borderRadius={'sm'} colorPalette={'purple'} variant={'subtle'}>
                  {match}
                </Badge>
              ))}
            </HStack>
          </VStack>
        </VStack>
      </VStack>
    </motion.div>
  )
}

const ScrollingResults = () => {
  const { isVisible } = useAnimationContext()
  const investors: InvestorData[] = [
    {
      id: 1,
      name: 'Rabil Ventures',
      title: 'Venture Capital',
      location: 'USA',
      matches: ['Entertainment', 'Social Media', 'Mobile Apps'],
      matchScore: 100,
      avatarUrl:
        'https://scoaccounts.s3.us-east-2.amazonaws.com/airtable/attGCPmVRMZCb7jEJ/rabil_ventures_logo.jpg',
    },
    {
      id: 2,
      name: 'WndrCo',
      title: 'Angel Investor',
      location: 'UK',
      matches: ['FinTech', 'AI/ML', 'Healthcare'],
      matchScore: 97,
      avatarUrl:
        'https://scoaccounts.s3.us-east-2.amazonaws.com/airtable/attLnvbU7rsZpQ61N/6426f9f8ee2397f1e899f8de_wndrco.jpeg',
    },
    {
      id: 3,
      name: 'Waverley Capital',
      title: 'Venture Capital',
      location: 'Germany',
      matches: ['CleanTech', 'SaaS', 'E-commerce'],
      matchScore: 95,
      avatarUrl:
        'https://scoaccounts.s3.us-east-2.amazonaws.com/airtable/attHR5L34DVz14V7P/1630497913585.jpg',
    },
    {
      id: 4,
      name: 'Charles Smith',
      title: 'Angel Investor',
      location: 'Singapore',
      matches: ['EdTech', 'Gaming', 'AR/VR'],
      matchScore: 85,
      avatarUrl:
        'https://scoaccounts.s3.us-east-2.amazonaws.com/airtable/att1LhwWhbOnDfJcF/1516234404204.jpg',
    },
    {
      id: 5,
      name: 'Ludlow Ventures',
      title: 'Venture Capital',
      location: 'Canada',
      matches: ['Biotech', 'IoT', 'Robotics'],
      matchScore: 83,
      avatarUrl:
        'https://scoaccounts.s3.us-east-2.amazonaws.com/airtable/attFJfb9ravRICqHi/htxfx3gafwntsn6mbsqb.webp',
    },
    {
      id: 6,
      name: 'Krillion Ventures',
      title: 'Angel Investor',
      location: 'Australia',
      matches: ['Cybersecurity', 'Blockchain', 'AI/ML'],
      matchScore: 79,
      avatarUrl:
        'https://scoaccounts.s3.us-east-2.amazonaws.com/airtable/att7RnOVVxMYe43s4/1631341170357.jpg',
    },
  ]

  const [selectedInvestor, setSelectedInvestor] = useState<InvestorData | null>(null)
  const maxScroll = 400

  return (
    <Box height={'100%'} overflow={'hidden'} position={'relative'} width={'full'}>
      <motion.div
        animate={isVisible ? { y: [0, 0, -maxScroll, 0] } : { y: 0 }}
        initial={{ y: 0 }}
        style={{
          position: 'absolute',
          width: '100%',
        }}
        transition={{
          duration: 7,
          times: [0, 0.2, 0.6, 1],
          ease: 'easeInOut',
        }}
      >
        <VStack gap={4} width={'100%'}>
          {investors.map((investor, index) => (
            <motion.div
              key={investor.id}
              initial={{ opacity: 0, scale: 0.95, rotateX: -10 }}
              animate={{
                opacity: 1,
                scale: 1,
                rotateX: 0,
                y: [20, 0],
              }}
              style={{
                width: '100%',
                transformOrigin: 'center center',
                perspective: '1000px',
              }}
              transition={{
                duration: 0.8,
                delay: 0.15 * index,
                ease: [0.34, 1.56, 0.64, 1],
                times: [0, 0.6, 1],
                opacity: { duration: 0.4 },
                rotateX: { duration: 0.6 },
              }}
            >
              <Box
                alignItems={'center'}
                bgColor={'colorPalette.muted'}
                borderRadius={'md'}
                boxShadow={'sm'}
                display={'flex'}
                justifyContent={'center'}
                overflow={'hidden'}
                position={'relative'}
                width={'100%'}
              >
                <motion.div
                  animate={{ x: '0%' }}
                  initial={{ x: '-100%' }}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                  }}
                  transition={{
                    duration: 0.6,
                    delay: 0.15 * index + 0.4,
                    ease: 'easeOut',
                  }}
                />
                <HStack align={'flex-start'} gap={0} height={'full'} padding={4} width={'full'}>
                  <HStack align={'center'} gap={2} height={'full'} justify={'space-between'} width={'full'}>
                    <VStack align={'flex-start'} height={'full'} justify={'center'} width={'full'}>
                      <HStack gap={4}>
                        <Box position={'relative'}>
                          <Avatar.Root borderRadius={'lg'} size={'lg'}>
                            <Avatar.Fallback borderRadius={'lg'} />
                            <Avatar.Image borderRadius={'lg'} src={investor.avatarUrl} />
                          </Avatar.Root>
                          <Flex
                            align={'center'}
                            bgColor={'green'}
                            borderRadius={'full'}
                            bottom={'-3px'}
                            boxShadow={'md'}
                            height={'20px'}
                            justify={'center'}
                            position={'absolute'}
                            right={'-3px'}
                            width={'20px'}
                          >
                            <Icon asChild color={'white'}>
                              <LuCheck />
                            </Icon>
                          </Flex>
                        </Box>
                        <VStack align={'flex-start'} gap={0} justify={'flex-start'} padding={0}>
                          <Text color={'colorPalette.fg'} fontWeight={'semibold'} textStyle={'sm'}>
                            {investor.name}
                          </Text>
                          <HStack gap={2}>
                            <Text color={'colorPalette.fg/70'} textStyle={'xs'}>
                              {investor.title}
                            </Text>
                            <Separator
                              color={'colorPalette.border'}
                              height={'12px'}
                              orientation={'vertical'}
                            />
                            <Text color={'colorPalette.fg/70'} textStyle={'xs'}>
                              {investor.location}
                            </Text>
                          </HStack>
                        </VStack>
                      </HStack>
                      <HStack align={'center'} gap={1} height={'full'} justify={'flex-start'} width={'full'}>
                        <Text color={'colorPalette.border'} textStyle={'label2xs'}>
                          Matches:
                        </Text>
                        {investor.matches.map((match, idx) => (
                          <Badge
                            key={idx}
                            bgColor={'colorPalette.subtle'}
                            borderRadius={'sm'}
                            color={'colorPalette.fg'}
                            textStyle={'2xs'}
                            variant={'subtle'}
                          >
                            {match}
                          </Badge>
                        ))}
                      </HStack>
                    </VStack>
                    <ProgressCircle.Root colorPalette={'teal'} size={'md'} value={investor.matchScore}>
                      <ProgressCircle.Circle>
                        <ProgressCircle.Track />
                        <ProgressCircle.Range strokeLinecap={'round'} />
                      </ProgressCircle.Circle>
                    </ProgressCircle.Root>
                  </HStack>
                </HStack>
              </Box>
            </motion.div>
          ))}
        </VStack>
      </motion.div>
      <AnimatePresence>
        {selectedInvestor && (
          <motion.div
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            initial={{ opacity: 0 }}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'black',
              zIndex: 10,
            }}
          >
            <InvestorProfile investor={selectedInvestor} onClose={() => setSelectedInvestor(null)} />
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  )
}

export default SearchInvestorsAnim
